import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { getContrastYIQ } from '../utils/textUtils';

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: start;
  padding-top: 48px;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background: #323940;
  border-radius: 8px;
  width: 768px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  position: relative;

  &::-webkit-scrollbar {
      width: 8px;
  }

  &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 4px;

      &:hover {
          background: rgba(255, 255, 255, 0.3);
      }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    margin: 16px;
  }
`;

// Base components that might be extended by others later
export const StyledInput = styled.input`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 3px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  background: #22272B; // Matches Description and CommentInput background
  color: #B6C2CF;    // Matches text color

  &:focus {
    outline: 2px solid #4c9aff; // Matches Description focus
    box-shadow: none; // Remove default browser focus shadow if any
  }

  &::placeholder {
    color: #6B778C; // Matches NewItemInput placeholder
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #42526e; /* Default color, can be overridden */
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(9, 30, 66, 0.08); /* Default hover, can be overridden */
  }
`;


// Styles for Parent Task Dropdown
export const ParentDropdownContainer = styled.div`
  position: relative; /* Changed to relative to be part of the flow or absolute if wrapper is relative */
  background: #282E33;
  border: 1px solid #3B444C; /* Added border for definition */
  border-radius: 3px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  margin-top: 8px;
  /* padding: 8px; // Padding will be on inner elements or controlled by layout */
  z-index: 10;
  width: 100%;
  max-height: 250px; /* Adjusted max height */
  display: flex;
  flex-direction: column;
`;

export const ParentDropdownSearchInput = styled(StyledInput)`
  margin: 8px; /* Add margin around the input */
  padding: 8px 10px;
  font-size: 14px;
  width: calc(100% - 16px); /* Adjust width considering margin */
  margin-bottom: 4px; /* Less space if list is directly below */
  box-sizing: border-box;
`;

export const ParentDropdownList = styled.ul`
  list-style: none;
  padding: 0 4px 4px 4px; /* Padding for items not to touch edges */
  margin: 0;
  overflow-y: auto;
  flex-grow: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

export const ParentDropdownItem = styled.li`
  padding: 8px 10px;
  cursor: pointer;
  color: #B6C2CF;
  border-radius: 3px;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 4px 4px 4px; /* Margin for items within the padded list */

  &:hover {
    background-color: #3B444C;
  }

  &.disabled { /* This class might not be needed if items are pre-filtered */
    color: #6B778C;
    cursor: not-allowed;
    background-color: transparent;
  }
`;

export const ParentDropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 8px 4px 8px; /* Padding for the header area */
  border-bottom: 1px solid #3B444C; /* Separator */
  margin-bottom: 4px; /* Space before search input or list */
`;

export const ParentDropdownTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #B6C2CF;
`;


export const ParentDropdownCloseButton = styled(CloseButton)`
  /* Inherits from CloseButton, which is already absolutely positioned */
  /* We need to make sure ParentDropdownContainer or a wrapper is 'position: relative' */
  /* For simplicity, let's redefine a simpler close button for now if not using absolute */
  position: static; /* Override if CloseButton is too complex here */
  background: none;
  border: none;
  font-size: 18px; /* Smaller */
  color: #B6C2CF;
  padding: 0; /* Reset padding */
  width: auto; /* Auto width */
  height: auto; /* Auto height */
  line-height: 1;

  &:hover {
    color: #FFFFFF;
    background: none; /* Reset hover from CloseButton if it has one */
  }
`;


export const NoResultsMessage = styled.p`
  color: #8A94A0;
  font-size: 13px;
  text-align: center;
  padding: 10px; /* More padding */
  margin: 0;
`;

export const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px; // Add some space below the header
`;

export const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #B6C2CF;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 3px;

  &:hover {
    background-color: rgba(182, 194, 207, 0.1); // Subtle background on hover
  }

  svg {
    margin-right: 6px;
    width: 16px; // Adjust size as needed
    height: 16px; // Adjust size as needed
    transition: transform 0.2s ease-in-out;
    transform: ${props => props.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  }
`;

export const NextStatusButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  cursor: pointer;
  border-radius: 3px;
  margin-left: 4px;
  margin-bottom: 8px; /* Added margin-bottom */
  background: #3B444C; /* Match SidebarButton background */
  color: #B6C2CF; /* Match SidebarButton color */
  border: none; /* Ensure no default border */

  &:hover {
    background: #B6C2CF; /* Match SidebarButton hover background */
    color: #3b444c; /* Match SidebarButton hover color */
  }

  svg {
    width: 16px; /* Match SidebarButton SVG size */
    height: 16px; /* Match SidebarButton SVG size */
    stroke: currentColor; /* Ensure SVG color inherits and changes on hover */
  }
`;

export const MainContent = styled.div`
  flex: 1;
  padding: 24px;
  min-width: 0;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const Sidebar = styled.div`
  width: 200px;
  padding: 24px 16px;

  @media (max-width: 768px) {
    width: 100%;
    padding: 16px;
    border-top: 1px solid #3B444C;
  }
`;

// Original CloseButton definition removed, moved to top

export const Title = styled.textarea`
  width: 100%;
  font-size: 20px;
  font-weight: bold;
  border: none;
  margin-bottom: 8px;
  border-radius: 3px;
  background: transparent;
  color: #B6C2CF;
  white-space: normal;
  word-break: break-word;
  resize: vertical;
  overflow: hidden;

  &:focus {
    outline: none;
  }
`;

export const Section = styled.div`
  margin-bottom: 24px;
`;

export const SectionTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #B6C2CF;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
  }
`;

export const Description = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: none;
  resize: vertical;
  border-radius: 3px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  background: #22272B;
  color: #B6C2CF;

  &:focus {
    outline: 2px solid #4c9aff;
  }
`;

export const QuillEditorContainer = styled.div`
  .ql-toolbar {
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
    background: #2C333A;
    border: 1px solid #3B444C;
    color: #B6C2CF;
  }

  .ql-container {
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    background: #22272B;
    border: 1px solid #3B444C;
    color: #B6C2CF;
    font-size: 14px;
    min-height: 100px; /* For description */
  }

  .ql-editor {
    min-height: 100px; /* For description */
    color: #B6C2CF;
  }

  .ql-editor.ql-blank::before {
    color: #6B778C;
  }

  /* Minimalistic toolbar styling */
  .ql-toolbar.ql-snow .ql-formats {
    margin-right: 8px;
  }

  .ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
    background: #2C333A;
    border: 1px solid #3B444C;
    border-radius: 3px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .ql-toolbar.ql-snow .ql-picker-options .ql-picker-item:hover {
    background: #3B444C;
    color: #B6C2CF;
  }

  .ql-toolbar.ql-snow .ql-stroke {
    stroke: #B6C2CF;
  }

  .ql-toolbar.ql-snow .ql-fill {
    fill: #B6C2CF;
  }

  .ql-toolbar.ql-snow .ql-active .ql-stroke,
  .ql-toolbar.ql-snow .ql-active .ql-fill {
    stroke: #4c9aff;
    fill: #4c9aff;
  }

  .ql-tooltip {
    background-color: #2C333A;
    border: 1px solid #3B444C;
    color: #B6C2CF;
  }

  .ql-tooltip a.ql-action:hover {
    color: #4c9aff;
  }

  .ql-tooltip.ql-editing a.ql-action {
    color: #4c9aff;
  }

  .ql-tooltip input[type=text] {
    background: #22272B;
    border: 1px solid #3B444C;
    color: #B6C2CF;
  }
`;

export const HeaderSection = styled.div`
  margin-bottom: 24px;
`;

export const MetaSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
`;

export const Labels = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;

  @media (max-width: 768px) {
    gap: 4px;
  }
`;

export const Label = styled.span`
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => getContrastYIQ(props.color)};
  background-color: ${props => props.color};
  cursor: pointer;
`;

export const AddLabel = styled.button`
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 12px;
  border: none;
  background: #ebecf0;
  cursor: pointer;
  color: #42526e;

  &:hover {
    background: #dfe1e6;
  }
`;

export const ChecklistContainer = styled.div`
  margin-bottom: 24px;
  padding: 8px;
  background: #22272B;
  border-radius: 3px;
`;

export const ChecklistHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

export const ChecklistToggleButton = styled.button`
  background: none;
  border: none;
  color: #B6C2CF;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 3px;

  &:hover {
    background-color: rgba(182, 194, 207, 0.1); // Subtle background on hover
  }

  svg {
    margin-right: 6px;
    width: 16px; // Adjust size as needed
    height: 16px; // Adjust size as needed
    transition: transform 0.2s ease-in-out;
  }
`;

export const ChecklistTitle = styled.input`
  font-weight: bold;
  font-size: 14px;
  border: none;
  background: transparent;
  padding: 4px;
  flex-grow: 1;
  color: #B6C2CF;
  
  &:focus {
    outline: none;
    border-radius: 3px;
  }
`;

export const Progress = styled.div`
  height: 8px;
  background: #B6C2CF;
  border-radius: 4px;
  margin: 8px 0;
  overflow: hidden;
`;

export const ProgressBar = styled.div`
  height: 100%;
  background: ${props => props.percentage === 100 ? '#57d9a3' : '#4c9aff'};
  width: ${props => props.percentage}%;
  transition: width 0.2s ease;
`;

export const NewItemInputContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  border: 1px solid #3B444C;
  border-radius: 3px;
  padding-right: 8px;

  &:focus-within {
    border-color: #4c9aff;
    outline: 1px solid #4c9aff; // Mimic original focus, but on container
  }
`;

export const NewItemInput = styled.input`
  flex-grow: 1;
  padding: 8px;
  border: none;
  border-radius: 3px; /* Keep radius for consistency if container padding is 0 */
  font-size: 14px;
  background: transparent;
  outline: none;
  color: #B6C2CF;

  &::placeholder {
    color: #6B778C; // Matching Quill placeholder color
  }

  &:focus {
    outline: none; // Focus handled by container
  }
`;

export const AddItemButton = styled.button`
  background: transparent;
  border: none;
  color: #B6C2CF; // Icon color (using a common theme color)
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;

  &:hover {
    color: #FFFFFF; // Brighter icon color on hover
    background-color: rgba(182, 194, 207, 0.1); // Subtle background on hover
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const TimelineSection = styled.div`
  margin-top: 24px;

  & > button { // Targeting the filter button
    margin-bottom: 12px; // Add some space below the filter button
    background-color: #3B444C; // A slightly different background to distinguish

    &:hover {
      background-color: #4C5A67; // Darken on hover
    }
  }
`;

export const TimelineItem = styled.div`
  padding: 8px;
  border-radius: 3px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #B6C2CF;
  display: flex;
  align-items: start;

  @media (max-width: 768px) {
    padding: 6px;
    font-size: 13px;
  }
`;

export const ActivityIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #22272B;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
`;

export const ActivityContent = styled.div`
  flex: 1;
  color: B6C2CF;
`;

export const ActivityMeta = styled.div`
  font-size: 12px; /* Original font-size */
  color: #B6C2CF; /* Original color */
  margin-top: 4px;
  display: flex; /* Align items in a row */
  align-items: center; /* Vertically align items */

  svg { /* Styling for ViewIcon in ActivityMeta */
    cursor: pointer;
    font-size: 1.2em; /* Slightly larger for visibility, relative to 12px */
    margin-left: 8px; /* Space from the timestamp or (edited) text */
  }
  svg:hover {
    opacity: 0.7;
  }

  span { /* Ensure (edited) text and icons align well */
      display: inline-flex;
      align-items: center;
  }
`;

export const CommentInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 0px solid #dfe1e6;
  border-radius: 3px;
  resize: vertical;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  background: #22272B;
  color: #B6C2CF;

  &:focus {
    border-color: #4c9aff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(76, 154, 255, 0.2);
  }
`;

export const Button = styled.button`
  padding: 6px 12px;
  background: #0079bf;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 14px;
  margin-right: 8px;

  &:hover {
    background: #026aa7;
  }

  &:disabled {
    background: #091e420a;
    cursor: not-allowed;
  }
`;

export const SidebarButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: #3B444C;
  border: none;
  border-radius: 3px;
  color: #B6C2CF;
  font-size: 14px;
  text-align: left;
  margin-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background: #B6C2CF;
    color: #3b444c
  }

  svg {
    margin-right: 8px;
    width: 16px;
    height: 16px;
  }
`;

export const AssigneeSection = styled.div`
  position: relative;
`;

export const AssigneeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #3B444C;
  border: none;
  border-radius: 3px;
  color: #B6C2CF;
  cursor: pointer;
  width: 100%;
  text-align: left;

  &:hover {
    background: #B6C2CF;
    color: #3b444c;
  }
`;

export const AssigneeAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.src ? 'none' : '#dfe1e6'};
  background-image: ${props => props.src ? `url(${props.src})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #172b4d;
  font-weight: bold;
  font-size: 12px;
`;

export const AssigneeDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #282E33;
    border-radius: 3px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    margin-top: 4px;
    z-index: 1000;
`;

export const AssigneeOption = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #3B444C;
  }
`;

export const MemberAvatar = styled(AssigneeAvatar)`
  width: 32px;
  height: 32px;
  margin-right: 8px;
`;

export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const UserName = styled.span`
  font-size: 14px;
  color: #B6C2CF;
`;

export const AssigneeDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: #22272B;
  border-radius: 3px;
  min-width: fit-content;
  cursor: pointer;

  &:hover {
    background: #2C333A;
  }
`;

export const AssigneeLabel = styled.span`
  color: #B6C2CF;
  font-size: 14px;
  font-weight: 500;
`;

export const StatusPrioritySection = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

export const FieldContainer = styled.div`
  flex: 1;
`;

export const FieldLabel = styled.div`
  color: #B6C2CF;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
`;

export const SelectButton = styled.button`
  width: 100%;
  padding: 6px 12px;
  background: #22272B;
  border: none;
  border-radius: 3px;
  color: #B6C2CF;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &:hover {
    background: #2C333A;
  }
`;

export const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #282E33;
  border-radius: 3px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  z-index: 10;
`;

export const DropdownOption = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #B6C2CF;

  &:hover {
    background: #3B444C;
  }

  ${props => props.selected && `
    background: #3B444C;
  `}
`;

export const PriorityIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => {
        switch (props.priority) {
            case 'immediate': return '#FF5630';
            case 'high': return '#FF8B00';
            case 'normal': return '#36B37E';
            case 'low': return '#6B778C';
            default: return '#6B778C';
        }
    }};
`;

export const DueDatePopover = styled.div`
  position: absolute;
  right: 24px;
  background: #282E33; // Darker background for popover
  border: 1px solid #3B444C; // Border color matching theme
  border-radius: 4px;
  padding: 16px;
  z-index: 1010; // Ensure it's above other elements
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  color: #B6C2CF; // Text color

  .react-datepicker {
    background-color: transparent;
    border: none;
  }

  .react-datepicker__header {
    background-color: #323940; // Updated header background
    border-bottom: 1px solid #2C333A; // Adjusted border to complement new background
  }

  .react-datepicker__current-month,
  .react-datepicker-year-header {
    color: #B6C2CF; // Ensure this remains for month/year text
  }

  .react-datepicker-time__header {
    background-color: #323940; // Explicitly set background for time header
    color: #B6C2CF; // Ensure text color
    // It might inherit from .react-datepicker__header, but being explicit is safer.
    // Also, check if it has a border that needs adjustment, e.g., border-bottom: none; or match other headers.
    // Based on default react-datepicker styles, it might have a top border if it's separate.
    // For now, I'll assume it doesn't need a specific border change beyond its background.
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    color: #B6C2CF; // Day and time text color
  }

  .react-datepicker__day:hover,
  .react-datepicker__month-text:hover,
  .react-datepicker__quarter-text:hover,
  .react-datepicker__year-text:hover {
    background-color: #3B444C; // Hover background for days
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--in-range,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__year-text--selected,
  .react-datepicker__year-text--in-selecting-range,
  .react-datepicker__year-text--in-range {
    background-color: #0079BF; // Selected day background
    color: white;
  }

  .react-datepicker__day--keyboard-selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__year-text--keyboard-selected {
    background-color: #0079BF; // Keyboard selected day background
    color: white;
  }

  .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {
    background-color: #0079BF !important; // Selected time background
    color: white !important;
  }

  .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover {
    background-color: #3B444C !important; // Hover time background
  }

  .react-datepicker__time-container {
    .react-datepicker__time {
      .react-datepicker__time-box {
        ul.react-datepicker__time-list {
          background-color: #323940; // Main modal content background

          /* Scrollbar styles for Firefox */
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);

          /* Scrollbar styles for Webkit browsers (Chrome, Safari, Edge) */
          &::-webkit-scrollbar {
            width: 8px;
          }

          &::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 4px;
          }

          &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 4px;

            &:hover {
              background: rgba(255, 255, 255, 0.3);
            }
          }

          li.react-datepicker__time-list-item {
            color: #B6C2CF; // Ensure text color consistency

            &:hover {
              background-color: #3B444C !important; // Keep existing hover from designs
              color: white !important; // Keep existing hover text color
            }

            &.react-datepicker__time-list-item--selected {
              background-color: #0079BF !important; // Keep existing selected background
              color: white !important; // Keep existing selected text color
            }
          }
        }
      }
    }
  }
`;

export const DueDateButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
`;

export const DueDateButton = styled(Button)`
  flex-grow: 1;
  margin: 0 4px;

  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }
`;

export const DueDateRemoveButton = styled(DueDateButton)`
  background-color: #FF5630; // A distinct color for remove/delete actions

  &:hover {
    background-color: #C70000; // Darker shade for hover
  }
`;

export const PopoverCloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #B6C2CF;
  line-height: 1;

  &:hover {
    color: #FFFFFF;
  }
`;

export const UserLink = styled(Link)`
  text-decoration: none;
  color: inherit; /* Use the parent's color */
  cursor: pointer;

  &:hover {
    text-decoration: underline; /* Underline on hover for clarity */
  }
`;

// New Styles for Cover Image Wrapper and Direct Remove Button
export const CoverImageWrapper = styled.div`
    position: relative;
    margin-bottom: 16px; // Same as ModalCoverImage had
    line-height: 0; // To prevent extra space below image
`;

export const DirectRemoveCoverButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: rgba(0, 0, 0, 0.8);
    }
`;

export const EditCommentActions = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 8px;
`;

export const CommentTextContainer = styled.div`
    display: flex; /* Changed to flex row by default */
    flex-direction: row; /* Explicitly set to row */
    align-items: flex-start; /* Align items to the start of the cross axis */
    gap: 8px;

    /* Text content part */
    & > div:first-child {
        flex-grow: 1; /* Allow text to take available space */
    }

    /* Style for icons (EditIcon) within CommentTextContainer */
    svg {
        cursor: pointer;
        font-size: 1em; /* Adjust size as needed */
        margin-left: 4px; /* Small space from the text */
        flex-shrink: 0; /* Prevent icon from shrinking */
    }
    svg:hover {
        opacity: 0.7;
    }
`;

/* Removed duplicate ActivityMeta definition. Styles were merged into the original. */

// Original StyledInput definition removed, moved to top

export const UnassignButton = styled.button`
  background: none;
  border: none;
  color: #B6C2CF; // Theme-appropriate color
  cursor: pointer;
  font-size: 20px; // Made slightly larger for easier clicking
  padding: 0 4px;
  line-height: 1;
  margin-left: 4px; // Add some space from the assignee name

  &:hover {
    color: #FF5630; // Danger/remove color on hover
  }
`;

export const ModalCoverImage = styled.img`
  width: 100%; /* Full width of the modal's main content area */
  max-height: 250px; /* Or a suitable max height */
  object-fit: cover;
  border-top-left-radius: 8px; /* Match modal border radius if at the very top */
  border-top-right-radius: 8px; /* Match modal border radius if at the very top */
  margin-bottom: 16px; /* Space below the cover image */
  background-color: #22272B; /* Placeholder background if image is transparent or loading */
`;

// Styles for Cover Image Popover
export const CoverImagePopover = styled.div`
  position: absolute;
  right: 220px; // Position it to the left of the sidebar
  top: 150px; // Adjust as needed
  background: #282E33;
  border-radius: 3px;
  box-shadow: 0 8px 16px rgba(0,0,0,0.3);
  padding: 16px;
  z-index: 1010; // Ensure it's above other modal content but below things like confirmation modals
  width: 300px; // Or adjust as needed
  color: #B6C2CF;
`;

export const CoverImagePreview = styled.img`
  width: 100%;
  max-height: 150px;
  object-fit: cover;
  border-radius: 3px;
  margin-bottom: 12px;
`;

export const UploadButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: #3B444C;
  border: none;
  border-radius: 3px;
  color: #B6C2CF;
  font-size: 14px;
  text-align: center;
  margin-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: #4C5A67;
  }
`;

export const RemoveCoverButton = styled(UploadButton)`
  background: #AE2A19; // A reddish color for removal
  &:hover {
    background: #8B2213;
  }
`;


// Styles for Clickable GitHub Link in CardModal
export const ClickableLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative; /* For absolute positioning of the edit button */
  padding: 4px; /* Similar to input padding for alignment if input is also padded */
  /* margin-bottom: 8px; */ /* Match StyledInput margin if necessary */

  &:hover .edit-link-button-class { /* Assuming EditLinkButton gets this class */
    opacity: 1;
  }
`;

export const LinkDisplay = styled.a`
  font-size: 14px; /* Match StyledInput font-size */
  color: #ADD8E6; /* Standard link color, e.g., light blue */
  text-decoration: underline;
  cursor: pointer;
  word-break: break-all; /* Break long URLs */
  flex-grow: 1; /* Allow link to take space */
  padding: 8px 0; /* Vertical padding to match input's perceived height */


  &:hover {
    color: #87CEEB; /* Lighter blue on hover */
  }
`;

export const EditLinkButton = styled.button`
  background: rgba(255, 255, 255, 0.1); /* Subtle background */
  border: none;
  border-radius: 3px;
  color: #B6C2CF; /* Icon color */
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0; /* Hidden by default, shown on wrapper hover */
  transition: opacity 0.2s ease-in-out;
  /* position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%); */

  ${ClickableLinkWrapper}:hover & {
    opacity: 1;
  }

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2); /* Slightly more visible on hover */
  }
`;

// Styles for Card Relations Section
export const RelationsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #3B444C; // Separator line
`;

export const RelationsSectionTitle = styled.h3` // Changed from h4 to h3 for more emphasis
  margin: 0;
  color: #B6C2CF;
  font-size: 16px; // Slightly larger than SectionTitle
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg { // ChevronIcon
    margin-right: 8px;
    transition: transform 0.2s ease-in-out;
    transform: ${props => props.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}; // Assuming default is right arrow
  }
`;

export const AddRelationButton = styled(Button)` // Reusing Button style
  padding: 6px 10px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #3B444C; // A neutral background

  &:hover {
    background-color: #4C5A67;
  }

  svg { // PlusIcon
    width: 16px;
    height: 16px;
  }
`;

export const SectionTitleWithAdd = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px; // Keep consistent with SectionTitle's original margin
`;

export const SmallIconButton = styled.button`
  background: transparent;
  border: none;
  color: #B6C2CF;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;

  &:hover {
    color: #FFFFFF;
    background-color: rgba(182, 194, 207, 0.1);
  }

  svg { // PlusIcon inside these buttons
    width: 18px; // Slightly larger for visibility as an icon button
    height: 18px;
  }
`;

// Generic scrollbar styling to be applied via className
export const CustomScrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  .custom-scrollbar { /* For Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
  }
`;

export const MobileSidebarContainer = styled.div`
  margin-bottom: 24px;
  border: 1px solid #3B444C;
  border-radius: 3px;
`;

export const MobileSidebarHeader = styled.div`
  padding: 12px;
  cursor: pointer;
  background: #2C333A;

  &:hover {
    background: #3B444C;
  }
`;

export const MobileSidebarContent = styled.div`
  max-height: ${props => (props.isExpanded ? '1000px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  padding: ${props => (props.isExpanded ? '12px' : '0 12px')};
`;

export const RelationTypeDropdown = styled.div`
  position: absolute;
  top: 100%; /* Position below the button that triggers it */
  right: 0; /* Align to the right of the button container or specific button */
  background: #282E33;
  border-radius: 3px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1020; // Higher than modal content, lower than other modals if any overlap
  margin-top: 4px;
  min-width: 180px; // Ensure enough width for items
`;

export const RelationTypeDropdownItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  color: #B6C2CF;
  font-size: 14px;

  &:hover {
    background-color: #3B444C;
  }
`;

// Note: To apply CustomScrollbarStyles, you would typically inject it globally
// or ensure your build process handles it. For styled-components, if you want to apply
// it to specific components, you'd pass it as a string to those components' styles.
// However, since we're using a className "custom-scrollbar", this CSS string
// would ideally be injected globally, for example, in your main `index.css` or via
// `createGlobalStyle` from styled-components.

// For the purpose of this exercise, I am assuming this CSS string will be handled.
// If it needs to be applied directly to styled-components, the approach would differ.
